<template>
  <v-navigation-drawer
    :value="drawer"
    class="hotspot-drawer"
    width="50vw"
    :height="getDrawerHeight()"
    disable-route-watcher
    hide-overlay
    absolute
    right
    :class="getAppName()"
  >
    <div v-if="hasArticle" class="hotspot-drawer-header">
      <div class="hotspot-drawer-header-button caret">
        <div
          v-if="articleStack.length > 1"
          @click="removeArticleStack(selectedArticle)"
        >
          <BaseIcon icon="arrow-left" hover />
        </div>
      </div>

      <div class="hotspot-drawer-header-title">
        <h2>{{ selectedArticle.title }}</h2>
      </div>

      <div>
        <div class="hotspot-drawer-header-button mr-10">
          <div @click="handleDrawerClose">
            <BaseIcon icon="close" hover />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="hasArticle"
      class="hotspot-drawer-scroll hotspot-drawer-wrapper"
      :class="{ 'hotspot-drawer-title-overlay': hasMedia }"
    >
      <div class="hotspot-drawer-content">
        <div v-if="hasMedia" class="hotspot-image">
          <MediaRotater :media-items="selectedArticle.media_items">
            <div slot="share-link-icon">
              <span @click="shareArticle()">
                <BaseIcon size="40px" icon="share" hover />
              </span>
            </div>
          </MediaRotater>
        </div>

        <div v-else class="share-link-container">
          <div slot="share-link-icon">
            <span @click="shareArticle()">
              <BaseIcon size="40px" icon="share" hover />
            </span>
          </div>
        </div>

        <div id="article-content" class="content-wrapper">
          <div class="hotspot-article">
            <article>
              <p
                v-target-blank-html="selectedArticle.content_html"
                class="mt-8"
              ></p>
            </article>
          </div>
        </div>
      </div>

      <CommentBox
        :article-id="selectedArticle.id"
        :comments="selectedArticleComments"
      />
    </div>

    <v-snackbar v-model="snackbar" absolute timeout="3000">
      <template v-slot:action>
        <div class="pr-2">
          <BaseIcon size="16px" icon="link" />
        </div>
      </template>
      <span>{{ $t("articleDrawer.link-copied") }}</span>
    </v-snackbar>

    <div v-if="hasArticle && hasRelatedArticles" class="hotspot-drawer-footer">
      <div class="hotspot-drawer-wrapper">
        <div class="hotspot-drawer-footer-header">
          <h3
            :style="{
              color: `${getFooterHeaderColor}`
            }"
          >
            {{ $t("articleDrawer.related-articles") }}
          </h3>

          <span :class="`divider ${getAppName()}`" />
        </div>
        <div id="menu">
          <ul>
            <li
              v-for="hotspot in selectedArticle.related_articles"
              :key="`article_${hotspot.id}_${hotspot.title}`"
              @click="addArticleStack(hotspot)"
            >
              <h4>
                <span class="menu-item-accent">#</span>
                {{ hotspot.slug != "" ? hotspot.slug : hotspot.title }}
              </h4>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import MediaRotater from "@/components/MediaRotater.vue";
import CommentBox from "@/components/CommentBox.vue";
import { cloneDeep } from "lodash";
import copy from "copy-to-clipboard";
import { appIdentifier, appUrl } from "@/utils/appIdentifier.js";

export default {
  name: "ArticleDrawer",
  components: {
    CommentBox,
    MediaRotater
  },
  directives: {
    "target-blank-html": function(el, binding) {
      el.innerHTML = binding.value;

      for (const a of el.getElementsByTagName("a")) {
        a.setAttribute("target", "_blank");
      }
    }
  },
  data: function() {
    return {
      snackbar: false
    };
  },
  watch: {
    articleDrawer() {
      this.scrollToTop();
    },
    mainMenuDrawer() {
      this.$store.commit(this.$mutations.TOGGLE_ARTICLE_DRAWER, false);
    },
    selectedArticle(newVal, oldVal) {
      this.scrollToTop();
      //Open Article drawer if not currently open
      if (newVal != oldVal && this.articleDrawer != true) {
        this.$store.commit(this.$mutations.TOGGLE_ARTICLE_DRAWER, true);
      }
    },
    articleStack(newVal, oldVal) {
      //Select the current selected article from stack
      if (newVal.length > 0 && newVal.length != oldVal.length) {
        this.$store.commit(
          this.$mutations.SET_ARTICLE,
          newVal[newVal.length - 1]
        );
      }

      //Get article if 'incomplete'
      if (newVal.length > 0 && !newVal[newVal.length - 1]["content"]) {
        this.$store
          .dispatch(this.$actions.ADD_ARTICLE, newVal[newVal.length - 1].id)
          .then(related_article => {
            this.$store.commit(this.$mutations.SET_ARTICLE, related_article);
          });
      }
    }
  },
  computed: {
    hasArticle() {
      return Object.entries(this.selectedArticle).length > 0;
    },
    hasMedia() {
      return this.selectedArticle.media_items.length > 0;
    },
    hasRelatedArticles() {
      return this.selectedArticle.related_articles.length > 0;
    },
    getFooterHeaderColor() {
      if (appIdentifier() == "dordrecht5D") {
        return "#FFFFFF";
      } else if (appIdentifier() == "canals5D") {
        return "#2354E";
      }

      return "secondary";
    },
    ...mapState({
      articleDrawer: state => state.articleDrawer,
      mainMenuDrawer: state => state.openMenuDrawer,
      selectedArticle: state => state.selectedArticle,
      selectedArticleComments: state => state.selectedArticleComments,
      articleStack: state => cloneDeep(state.articleStack)
    }),
    ...mapGetters({
      drawer: "getArticleDrawer"
    })
  },
  methods: {
    scrollToTop() {
      document
        .querySelectorAll(".hotspot-drawer-scroll")
        .forEach(e => (e.scrollTop = 0));
    },
    getDrawerHeight() {
      if (
        this.$route.name == "map-view" ||
        this.$route.name == "article-share"
      ) {
        return `calc(100vh - ${70}px)`;
      }
      return "100vh";
    },
    shareArticle() {
      this.snackbar = true;
      copy(`${appUrl()}/article/${this.selectedArticle.id}`);
    },
    addArticleStack(article) {
      this.$store.commit(this.$mutations.ADD_ARTICLE_STACK, article);
    },
    removeArticleStack(selectedArticle) {
      this.$store.commit(
        this.$mutations.REMOVE_ARTICLE_STACK,
        selectedArticle.id
      );
    },
    handleDrawerClose: function() {
      this.$store.commit(this.$mutations.TOGGLE_ARTICLE_DRAWER, false);
      this.$store.commit(this.$mutations.CLEAR_ARTICLE_STACK);
    },
    getAppName() {
      return appIdentifier();
    }
  }
};
</script>

<style lang="scss" scoped>
.v-snack__content {
  span {
    font-size: 1.3rem;
  }
}

.share-link-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 50px;
  padding: 10px 5px;
}

.caret {
  margin-left: calc(8% - 20px);
}

.content-wrapper {
  position: relative;
  width: 80%;
}

.v-navigation-drawer {
  z-index: 10;
  background-color: var(--v-brand-base);
  direction: ltr;
  position: absolute !important;
  top: auto !important;
  bottom: 0 !important;
  border-bottom-left-radius: 50px;

  .hotspot {
    &-article {
      height: 100%;
      width: 100%;
      margin-bottom: 65px;
    }

    &-image {
      position: relative;
    }
  }

  ::v-deep .v-navigation-drawer__content {
    display: flex;
    flex-flow: column;
  }

  .v-navigation-drawer__content {
    .hotspot-drawer {
      height: 100%;
      position: relative;

      &-scroll {
        flex-grow: 1;
        overflow-y: auto;
        margin-right: 20px;
      }

      &-title-overlay {
        border-top-left-radius: 120px;
      }

      &-wrapper {
        margin-left: 8%;
      }

      &-header {
        flex-shrink: 0;
        width: 100%;
        background: var(--v-brand-base);
        z-index: 5;
        display: flex;
        align-items: center;
        text-align: center;
        height: 80px;

        &-title {
          width: calc(100% + 20px);
        }

        &-button {
          height: 20px;
          width: 20px;

          &:hover {
            cursor: pointer;
          }
        }
      }

      &-footer {
        margin-right: 20px;
        background: var(--v-brand-base);
        padding: 25px 0;

        &-header {
          width: 80%;
          height: 27px;
          margin-bottom: 3px;
          display: flex;

          span {
            display: block;
          }

          .divider {
            flex: auto;
            height: 20px;
            margin-left: 15px;
            border-bottom: 1px solid var(--v-secondary-base);

            &.canals5D {
              border-bottom: 1px solid rgba($secondary-canals5D, 0.4);
            }
          }
        }

        #menu {
          width: 82%;
          background: var(--v-brand-base);

          ul {
            padding: 0;
            margin: 5px 0 !important;

            li {
              display: inline-block;
              padding: 0 20px 0 0;

              &:hover {
                cursor: pointer;
              }

              span {
                color: var(--v-secondary-base);
              }
            }
          }
        }
      }
    }
  }
}
</style>
